import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { appSessionResetEventName } from '../utils/resetAppSession';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    const currentTheme = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) ||
      (typeof localStorage !== 'undefined' && (localStorage.getItem('unified-workforce-theme') || localStorage.getItem('workfolio-theme'))) ||
      'light';
    return {
      theme: currentTheme,
      toggleTheme: () => {},
      setTheme: () => {},
    };
  }
  return context;
};

export const ThemeProvider = ({ children, forcedTheme }) => {
  const [themeState, setThemeState] = useState(() => {
    if (forcedTheme) return forcedTheme;
    const savedTheme = localStorage.getItem('unified-workforce-theme') || localStorage.getItem('workfolio-theme');
    return savedTheme || 'light';
  });

  const theme = forcedTheme || themeState;

  useEffect(() => {
    if (!forcedTheme) {
      localStorage.setItem('unified-workforce-theme', themeState);
      localStorage.removeItem('workfolio-theme');
    }
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme, themeState, forcedTheme]);

  // Synchronize across browser tabs
  useEffect(() => {
    if (forcedTheme) return undefined;
    const handleStorage = (event) => {
      if (event.key === 'unified-workforce-theme' && event.newValue) {
        setThemeState(event.newValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [forcedTheme]);

  useEffect(() => {
    if (forcedTheme) return undefined;
    const handleReset = () => {
      const preserved = localStorage.getItem('unified-workforce-theme') || localStorage.getItem('workfolio-theme');
      setThemeState(preserved || 'light');
    };
    window.addEventListener(appSessionResetEventName, handleReset);
    return () => window.removeEventListener(appSessionResetEventName, handleReset);
  }, [forcedTheme]);

  const toggleTheme = useCallback(() => {
    if (forcedTheme) return;
    setThemeState((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  }, [forcedTheme]);

  const setTheme = useCallback((newTheme) => {
    if (forcedTheme) return;
    const valid = (newTheme === 'dark' || newTheme === 'system') ? newTheme : 'light';
    setThemeState(valid);
  }, [forcedTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

